import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Resolve,
  RouterStateSnapshot,
} from '@angular/router';
import { ProductPricingService } from '@usf/ngrx-pricing';
import { ProductStateService, getProductAlternates } from '@usf/ngrx-product';
import { Product } from '@usf/product-types';
import { Observable, combineLatest } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { PATHS } from '../../../../shared/constants/paths';
import { NavigationHelperService } from '../../../../shared/helpers/navigation.helpers.service';
import { productsAreInState } from '../../../../shared/selectors/helpers/product-info.selectors.helper';
import { ProductService } from '../../../../shared/services/product/product.service';
import { ListDetailManagementService } from './list-detail-management.service';
import {
  NR_CUSTOMATTRIBUTE_SHOPPING_LIST_ID,
  NR_CUSTOMATTRIBUTE_SHOPPING_LIST_TYPE,
  NR_CUSTOMATTRIBUTE_SHOPPING_LIST_NUM_PRODUCTS,
} from '../../../../shared/constants/newrelic-customattribute-constants';
import {
  ListType,
  ListViewMode,
  ListViewService,
  SeparatedProductNumbers,
} from '@usf/ngrx-list';

@Injectable({
  providedIn: 'root',
})
export class ListDetailManagementResolverService
  implements Resolve<Observable<any>>
{
  loading = false;

  constructor(
    private listDetailManagementService: ListDetailManagementService,
    private productPricingService: ProductPricingService,
    private productService: ProductService,
    private productStateService: ProductStateService,
    private navigationHelperService: NavigationHelperService,
    private readonly listViewService: ListViewService,
  ) {}

  resolve(route: ActivatedRouteSnapshot, state?: RouterStateSnapshot): any {
    const listKey = this.listDetailManagementService.getListIdFromRoute(route);

    this.checkIfListExists(listKey, route);
  }

  checkIfListExists(
    listKey: { listId: string; listTypeId: string },
    route: ActivatedRouteSnapshot,
  ) {
    combineLatest([
      this.listDetailManagementService.areListStatesLoaded$(),
      this.listDetailManagementService.selectListHeader$(
        listKey.listTypeId,
        Number(listKey.listId),
      ),
    ])
      .pipe(filter(([listsAreLoaded, list]) => listsAreLoaded))
      .pipe(take(1))
      .subscribe(([listsAreLoaded, list]) => {
        if (!list) {
          this.navigationHelperService.routeTo(PATHS.LISTS, {
            getChanges: false,
          });
        } else {
          if (route.routeConfig.path.includes('detail')) {
            this.listDetailManagementService.displayListDetailsStartTrace(
              list.listKey.listTypeId,
              String(list.listKey.listId),
              list.listItemCount,
            );

            const newrelic = window['newrelic'];

            try {
              if (!!newrelic) {
                newrelic.setCustomAttribute(
                  NR_CUSTOMATTRIBUTE_SHOPPING_LIST_ID,
                  String(list.listKey.listId),
                );
                newrelic.setCustomAttribute(
                  NR_CUSTOMATTRIBUTE_SHOPPING_LIST_TYPE,
                  list.listKey.listTypeId,
                );
                newrelic.setCustomAttribute(
                  NR_CUSTOMATTRIBUTE_SHOPPING_LIST_NUM_PRODUCTS,
                  list.listItemCount,
                );
              }
            } catch (err) {
              console.log('Error in open list detail new relic event');
              console.log(err);
            }

            this.listViewService.selectList(
              Number(list.listKey.listId),
              list.listKey.listTypeId as ListType,
              ListViewMode.ordering,
            );
          } else {
            this.listViewService.selectList(
              Number(list.listKey.listId),
              list.listKey.listTypeId as ListType,
              ListViewMode.management,
            );
          }

          this.getProductAndPricingData$(listKey);
        }
      });
  }

  getProductAndPricingData$(listKey: { listId: string; listTypeId: string }) {
    this.listDetailManagementService
      .productNumbers$(listKey.listTypeId, listKey.listId)
      .pipe(take(1))
      .subscribe((separatedProductNumbers: SeparatedProductNumbers) => {
        if (separatedProductNumbers.usfProductNumbers.length > 0) {
          this.productStateService.loadProductsWithRetry(
            separatedProductNumbers.usfProductNumbers,
          );
          this.productPricingService.loadPrices(
            separatedProductNumbers.usfProductNumbers,
          );
          this.productService
            .getProducts(separatedProductNumbers.usfProductNumbers)
            .pipe(filter(products => productsAreInState(products)))
            .pipe(take(1))
            .subscribe(products => {
              this.getPricingForAlternativeProducts(products);
            });
        }
      });
  }

  getPricingForAlternativeProducts(products: Map<number, Product>) {
    const productArr = Array.from(products.values()).reduce((list, product) => {
      list.push({
        productNumber: product?.productNumber,
        summary: product?.summary,
        inventory: product?.inventory,
      } as Product);
      return list;
    }, new Array<Product>());
    const alternateProductNumbers = getProductAlternates(productArr);
    this.productPricingService.loadPrices(alternateProductNumbers);
  }
}
